import { CreateGuestCurator, IGuestCuratorRecord } from "@src/types/curator";
import { ApiResponse, SortingParams } from "@src/types/httpApi";
import { RoleType } from "@src/types/user";
import { IShowRecord } from "@src/types/show";
import httpApi from "@src/utils/httpApi";
import { AxiosResponse } from "axios";

// Fetch subcurators
export const fetchSubcurators = (
  parentCuratorId?: string,
  searchText?: string,
  roleType?: RoleType
): Promise<AxiosResponse<ApiResponse<IGuestCuratorRecord[]>>> => {
  const buildFilter: {
    name?: { $containsi: string | undefined };
    id?: { $containsi: string | undefined };
    documentId?: { $containsi: string | undefined };
    curators?: { documentId: { $eq: string | undefined } };
    $or?: Array<
      | { [key: string]: { $containsi: string | undefined } }
      | { shows: { title: { $containsi: string | undefined } } }
    >;
  } = {};

  if (searchText) {
    buildFilter["$or"] = [
      { name: { $containsi: searchText } },
      { id: { $containsi: searchText } },
      { documentId: { $containsi: searchText } },
      { shows: { title: { $containsi: searchText } } },
    ];
  }
  if (roleType !== "admin" && roleType !== "manager") {
    buildFilter.curators = {
      documentId: {
        $eq: parentCuratorId,
      },
    };
  } else {
    delete buildFilter.curators;
  }
  return httpApi.get(`/api/curators`, {
    params: {
      populate: {
        user: {
          fields: ["email"],
        },
        shows: {
          fields: ["title", "description"],
        },
        webCuratorCatalog: {
          fields: ["*"],
        },
        shareCover: {
          fields: ["*"],
        },
      },
      filters: buildFilter,
      pagination: {
        pageSize: 100,
      },
    },
  });
};

// Fetch curators for dropdown
export const fetchCuratorsForDropdown = async (
  parentCuratorId: number
): Promise<any> => {
  const response = await httpApi.get(`/api/curators`, {
    params: {
      populate: {
        webCuratorCatalog: {
          fields: ["*"],
        },
        shareCover: {
          fields: ["*"],
        },
        shows: {
          fields: ["title", "description"],
        },
      },
      filters: {
        curators: {
          id: {
            $eq: parentCuratorId,
          },
        },
        visible: {
          $eq: true,
        },
      },
    },
  });

  return response.data;
};

// Create guest curator
export const createGuestCurator = async (
  data: CreateGuestCurator & { parentCurator: string }
): Promise<AxiosResponse> => {
  return httpApi.post(`/api/curators/invite`, {
    curatorName: data.name,
    email: data.email,
    invitedById: data.parentCurator,
    showIds: data.shows,
    visible: true,
  });
};

// Update guest curator with file uploads
export const updateGuestCurator = async (
  data: CreateGuestCurator & { preserveIsSubcurator?: boolean }
): Promise<AxiosResponse> => {
  const dataJson: {
    name?: string;
    about?: string;
    notes?: string | null;
    visible: boolean;
    isSubcurator?: boolean;
    shows: {
      set: Array<{ documentId: string }>;
    };
  } = {
    visible: true,
    shows: {
      set: (data.shows || []).map((documentId) => ({ documentId })),
    },
  };
  // Only set isSubcurator if not preserving the original value
  if (!data.preserveIsSubcurator) {
    dataJson.isSubcurator = true;
  }
  if (data.name) {
    dataJson["name"] = data.name;
  }
  if (data.about) {
    dataJson["about"] = data.about;
  }
  if (data.notes !== undefined) {
    dataJson["notes"] = data.notes ?? null;
  }
  const uploadFile = async (
    file: File,
    field: string
  ): Promise<string | null> => {
    const fileFormData = new FormData();
    fileFormData.append("files", file);
    fileFormData.append("field", field);
    fileFormData.append("ref", "api::curator.curator");
    fileFormData.append("refId", String(data?.id ?? ""));

    try {
      const response = await httpApi.post("/api/upload", fileFormData);
      return response.data[0]?.documentId || response.data[0]?.id || null;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  };
  if (data.desktopPhoto) {
    await uploadFile(data.desktopPhoto, "webCuratorCatalog");
  }
  if (data.mobilePhoto) {
    await uploadFile(data.mobilePhoto, "shareCover");
  }
  return httpApi.put(`/api/curators/${data.documentId}`, {
    data: dataJson,
  });
};

// Fetch active curators with archived playlist count
// NOTE: Backend should include 'archivedPlaylistCount' in the response for each curator
// This field represents the count of archived playlists for that curator
export interface IActiveCuratorWithArchivedCount {
  id: number;
  documentId: string;
  name: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  visible: boolean;
  isSubcurator: boolean;
  archivedPlaylistCount: number; // Backend should populate this field directly
  about?: string;
  /** Email from curator.user (flat on response). Curators without linked user return null. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Admin/manager notes, max 250 chars. Not visible to curators in their profile. */
  notes?: string | null;
  show?: IShowRecord; // Single show object (not array)
  shows?: IShowRecord[]; // Keep for backwards compatibility with edit form
  user?: {
    email: string;
  };
  webCuratorCatalog?: {
    formats?: {
      thumbnail?: { url: string };
      small?: { url: string };
      medium?: { url: string };
      large?: { url: string };
    };
    url?: string;
  };
}

// Fetch active curators for management page
// This endpoint should return curators with archivedPlaylistCount included
export const fetchCuratorsForManagement = (
  searchText?: string,
  page?: number,
  pageSize?: number,
  sorting?: SortingParams
): Promise<AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>> => {
  const queryParams = new URLSearchParams();

  // Sorting (Strapi format: sort=field:asc or sort=field:desc)
  if (sorting?.field) {
    queryParams.append("sort", `${sorting.field}:${sorting.direction}`);
  }

  // Populate parameters
  queryParams.append("populate[user][fields][0]", "email");
  queryParams.append("populate[shows][fields][0]", "frequency");
  queryParams.append("populate[shows][fields][1]", "title");
  queryParams.append("populate[shows][fields][2]", "documentId");
  queryParams.append("populate[webCuratorCatalog][fields][0]", "*");

  // Filter parameters
  queryParams.append("filters[visible][$eq]", "true");
  queryParams.append("filters[isSubcurator][$eq]", "false");

  // Pagination parameters (using page and pageSize directly, not pagination[page])
  queryParams.append("page", String(page || 1));
  queryParams.append("pageSize", String(pageSize || 25));

  // Search filter
  if (searchText) {
    queryParams.append("filters[$or][0][name][$containsi]", searchText);
    queryParams.append("filters[$or][1][id][$containsi]", searchText);
    queryParams.append("filters[$or][2][documentId][$containsi]", searchText);
  }

  // Use new endpoint specifically for management
  return httpApi.get(`/api/curators/management?${queryParams.toString()}`);
};

// Legacy function - kept for backwards compatibility if needed
export const fetchActiveCuratorsWithArchivedCount = (
  searchText?: string,
  page?: number,
  pageSize?: number,
  sorting?: SortingParams
): Promise<AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>> => {
  return fetchCuratorsForManagement(searchText, page, pageSize, sorting);
};

export interface ICuratorMedia {
  id: number;
  documentId: string;
  name: string;
  mime: string;
  size: number;
  width: number;
  height: number;
  url: string;
}

// The endpoint clamps pageSize at 100, so this is the largest page we can ask
// for — and the fewest round-trips per curator.
const CURATOR_MEDIA_PAGE_SIZE = 100;

// A single upload can be referenced by several curator/playlist fields
// (webCuratorCatalog, shareCover, imageSquare, originalImage, …), so the
// endpoint happily returns the same file more than once. Those repeats don't
// just clutter the grid: they collide on the React key the gallery renders
// cards with, which drops photos from the page.
const dedupeCuratorMedia = (items: ICuratorMedia[]): ICuratorMedia[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = String(item?.documentId || item?.id || item?.url || "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// The endpoint pages at 25 by default, so a curator with more photos than that
// silently lost the rest. Walk every page and hand the caller the full set.
export const fetchCuratorMedia = async (
  curatorId: string
): Promise<AxiosResponse<ApiResponse<ICuratorMedia[]>>> => {
  const requestPage = (
    page: number
  ): Promise<AxiosResponse<ApiResponse<ICuratorMedia[]>>> =>
    httpApi.get(`/api/curators/${curatorId}/media`, {
      params: {
        type: "images",
        "pagination[page]": page,
        "pagination[pageSize]": CURATOR_MEDIA_PAGE_SIZE,
      },
    });

  const firstResponse = await requestPage(1);
  const pageCount = firstResponse.data?.meta?.pagination?.pageCount ?? 1;
  const items = [...(firstResponse.data?.data ?? [])];

  for (let page = 2; page <= pageCount; page += 1) {
    const response = await requestPage(page);
    items.push(...(response.data?.data ?? []));
  }

  return {
    ...firstResponse,
    data: { ...firstResponse.data, data: dedupeCuratorMedia(items) },
  };
};
